"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Home,
  MessageSquare,
  Users,
  BookOpen,
  Settings,
  GraduationCap,
  Video,
  Search,
  Hash,
} from "lucide-react";
import { useAuth } from "@/context/auth-context";
import { isTutor } from "@/lib/role-utils";

type PeopleResult = {
  id: string;
  username: string;
  displayName: string;
  image: string | null;
  certification?: string | null;
  location?: string | null;
  visibility?: "PUBLIC" | "PRIVATE";
  followStatus?: "NONE" | "PENDING" | "ACCEPTED";
};

const OPEN_SEARCH_EVENT = "certlynx:open-search";

const BASE_QUICK_LINKS = [
  { href: "/feed", label: "Home Feed", icon: Home },
  { href: "/forums", label: "Community Forums", icon: MessageSquare },
  { href: "/tutors", label: "Find a Tutor", icon: Users },
  { href: "/study-groups", label: "Study Groups", icon: BookOpen },
  { href: "/videos", label: "Videos", icon: Video },
  { href: "/settings", label: "Account Settings", icon: Settings },
] as const;

export function CommandPalette() {
  const { user } = useAuth();
  const router = useRouter();
  const quickLinks = isTutor(user)
    ? BASE_QUICK_LINKS.filter((l) => l.href !== "/study-groups")
    : [...BASE_QUICK_LINKS];
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [topicResults, setTopicResults] = useState<any[]>([]);
  const [certResults, setCertResults] = useState<string[]>([]);
  const [peopleResults, setPeopleResults] = useState<PeopleResult[]>([]);
  const [hashtagResults, setHashtagResults] = useState<{ tag: string; displayTag: string }[]>([]);
  const [searching, setSearching] = useState(false);

  // ⌘K / Ctrl+K shortcut, plus the navbar Search button on phones
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    const openSearch = () => setOpen(true);
    document.addEventListener("keydown", handler);
    window.addEventListener(OPEN_SEARCH_EVENT, openSearch);
    return () => {
      document.removeEventListener("keydown", handler);
      window.removeEventListener(OPEN_SEARCH_EVENT, openSearch);
    };
  }, []);

  // Debounced search across people + forums + certifications
  useEffect(() => {
    if (!search.trim()) {
      setTopicResults([]);
      setCertResults([]);
      setPeopleResults([]);
      setHashtagResults([]);
      return;
    }
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const tagQuery = search.trim().replace(/^#+/, "");
        const peopleUrl = `/api/search?q=${encodeURIComponent(search)}&type=people`;
        const [topicsRes, certsRes, peopleRes, hashtagRes] = await Promise.all([
          fetch(`/api/forums/topics?search=${encodeURIComponent(search)}&limit=5`),
          fetch("/api/certifications/list"),
          user ? fetch(peopleUrl) : Promise.resolve(null),
          fetch(`/api/hashtags?q=${encodeURIComponent(tagQuery)}&limit=5`),
        ]);
        const [topicsData, certsData, peopleData, hashtagData] = await Promise.all([
          topicsRes.json(),
          certsRes.json(),
          peopleRes?.ok ? peopleRes.json() : Promise.resolve({ people: [] }),
          hashtagRes.ok ? hashtagRes.json() : Promise.resolve({ hashtags: [] }),
        ]);
        setTopicResults(topicsData.topics?.slice(0, 5) ?? []);
        const allCerts: string[] = certsData.certifications ?? [];
        setCertResults(
          allCerts.filter((c) => c.toLowerCase().includes(search.toLowerCase())).slice(0, 5)
        );
        setPeopleResults(Array.isArray(peopleData.people) ? peopleData.people.slice(0, 5) : []);
        setHashtagResults(Array.isArray(hashtagData.hashtags) ? hashtagData.hashtags.slice(0, 5) : []);
      } catch {
        setTopicResults([]);
        setCertResults([]);
        setPeopleResults([]);
        setHashtagResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [search, user]);

  const navigate = (href: string) => {
    setOpen(false);
    setSearch("");
    router.push(href);
  };

  const typedTag = search.trim().replace(/^#+/, "").toLowerCase().replace(/[^a-z0-9_]/g, "");
  const showTypedTag =
    search.trim().startsWith("#") &&
    /^[a-z][a-z0-9_]{1,63}$/.test(typedTag) &&
    !hashtagResults.some((h) => h.tag === typedTag);
  const hasResults =
    topicResults.length > 0 ||
    certResults.length > 0 ||
    peopleResults.length > 0 ||
    hashtagResults.length > 0 ||
    showTypedTag;

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder="Search people, hashtags, forums, certifications, or navigate…"
        value={search}
        onValueChange={setSearch}
      />
      <CommandList>
        {!search && (
          <CommandGroup heading="Quick navigation">
            {quickLinks.map(({ href, label, icon: Icon }) => (
              <CommandItem key={href} value={label} onSelect={() => navigate(href)}>
                <Icon className="mr-2 h-4 w-4 text-muted-foreground" />
                {label}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {search && !searching && !hasResults && (
          <CommandEmpty>No results for &ldquo;{search}&rdquo;</CommandEmpty>
        )}

        {search && searching && (
          <div className="px-4 py-6 text-center text-sm text-muted-foreground">
            Searching…
          </div>
        )}

        {(hashtagResults.length > 0 || showTypedTag) && (
          <>
            <CommandGroup heading="Hashtags">
              {hashtagResults.map((h) => (
                <CommandItem
                  key={h.tag}
                  value={`#${h.displayTag} ${h.tag}`}
                  onSelect={() => navigate(`/hashtag/${h.tag}`)}
                >
                  <Hash className="mr-2 h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="truncate">#{h.displayTag}</span>
                </CommandItem>
              ))}
              {showTypedTag && (
                <CommandItem
                  value={`#${typedTag}`}
                  onSelect={() => navigate(`/hashtag/${typedTag}`)}
                >
                  <Hash className="mr-2 h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="truncate">#{typedTag}</span>
                </CommandItem>
              )}
            </CommandGroup>
            {(peopleResults.length > 0 || topicResults.length > 0 || certResults.length > 0) && <CommandSeparator />}
          </>
        )}

        {peopleResults.length > 0 && (
          <>
            <CommandGroup heading="People">
              {peopleResults.map((person) => (
                <CommandItem
                  key={person.id}
                  value={`person-${person.username}-${person.displayName}-${person.certification ?? ""}-${person.location ?? ""}`}
                  onSelect={() => navigate(`/social-profile/${person.username}`)}
                >
                  <Users className="mr-2 h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="truncate">{person.displayName}</span>
                  <span className="ml-2 shrink-0 text-xs text-muted-foreground">@{person.username}</span>
                  {person.visibility === "PRIVATE" && person.followStatus !== "ACCEPTED" ? (
                    <span className="ml-2 shrink-0 text-xs text-muted-foreground">Private</span>
                  ) : (person.certification || person.location) ? (
                    <span className="ml-2 truncate text-xs text-muted-foreground">
                      {[person.certification, person.location].filter(Boolean).join(" · ")}
                    </span>
                  ) : null}
                </CommandItem>
              ))}
            </CommandGroup>
            {(topicResults.length > 0 || certResults.length > 0) && <CommandSeparator />}
          </>
        )}

        {topicResults.length > 0 && (
          <>
            <CommandGroup heading="Forum discussions">
              {topicResults.map((t: any) => (
                <CommandItem
                  key={t.id}
                  value={`topic-${t.id}-${t.title}`}
                  onSelect={() => navigate(`/forums/${t.category?.slug ?? "general"}/${t.id}`)}
                >
                  <MessageSquare className="mr-2 h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="truncate">{t.title}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            {certResults.length > 0 && <CommandSeparator />}
          </>
        )}

        {certResults.length > 0 && (
          <CommandGroup heading="Certifications">
            {certResults.map((cert) => (
              <CommandItem
                key={cert}
                value={`cert-${cert}`}
                onSelect={() => navigate(`/forums?search=${encodeURIComponent(cert)}`)}
              >
                <GraduationCap className="mr-2 h-4 w-4 text-muted-foreground shrink-0" />
                {cert}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {search && hasResults && (
          <>
            <CommandSeparator />
            <CommandGroup>
              <CommandItem
                value={`search-all-${search}`}
                onSelect={() => navigate(`/forums?search=${encodeURIComponent(search)}`)}
              >
                <Search className="mr-2 h-4 w-4 text-muted-foreground" />
                Search &ldquo;{search}&rdquo; in all discussions
              </CommandItem>
            </CommandGroup>
          </>
        )}
      </CommandList>

      <div className="border-t px-3 py-2 flex items-center justify-between text-[11px] text-muted-foreground">
        <span>
          <kbd className="font-mono bg-muted rounded px-1 py-0.5">↑↓</kbd> navigate
          <span className="mx-1.5">·</span>
          <kbd className="font-mono bg-muted rounded px-1 py-0.5">↵</kbd> select
          <span className="mx-1.5">·</span>
          <kbd className="font-mono bg-muted rounded px-1 py-0.5">Esc</kbd> close
        </span>
        <span>
          <kbd className="font-mono bg-muted rounded px-1 py-0.5">⌘K</kbd>
        </span>
      </div>
    </CommandDialog>
  );
}
